import { ApiError, post } from './api.ts'

export type UploadedFile = {
  fileId: string
  kind: string
  mimeType: string
  sizeBytes: number
  /** Where the bytes landed. A product stores this directly; a patient
   *  image is referenced by its file id instead. */
  path: string
  previewUrl: string
}

type SignResponse = { fileId: string; path: string; uploadUrl: string; token: string }

/**
 * Bytes go straight from the browser to Supabase Storage with a one-off
 * signed URL, never through the API. A 3 MB image must not occupy a request
 * handler that a dozen record lookups are queued behind.
 *
 * No database row exists yet: during registration the patient is not
 * created until the final step, so the file rows are written in the same
 * transaction as the registration.
 */
export async function uploadImage(blob: Blob, kind: string): Promise<UploadedFile> {
  const mimeType = blob.type || 'image/jpeg'
  const sign = await post<SignResponse>('/api/files/sign', {
    kind, mimeType, sizeBytes: blob.size,
  })

  // Supabase expects the upload token as a QUERY PARAMETER on the signed
  // URL, not as a bearer header -- the header form is silently rejected.
  const target = new URL(sign.uploadUrl)
  target.searchParams.set('token', sign.token)

  const res = await fetch(target, {
    method: 'PUT',
    headers: { 'Content-Type': mimeType },
    body: blob,
  }).catch(() => null)

  if (!res || !res.ok) {
    const detail = res ? `Storage returned ${res.status}.` : 'The connection dropped.'
    throw new ApiError(0, 'UPLOAD_FAILED',
      `${detail} Capture the photo again.`)
  }

  return {
    fileId: sign.fileId,
    path: sign.path,
    kind,
    mimeType,
    sizeBytes: blob.size,
    // Local preview only -- revoked when the component unmounts. Nothing is
    // read back from storage during data entry.
    previewUrl: URL.createObjectURL(blob),
  }
}
